import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  UserIcon,
  FolderIcon,
  InformationCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge, Avatar } from "components/ui";
import clsx from "clsx";
import { formatFileSize, getVerificationMetadataLabels, getInitials, getDocumentUrl } from "../utils/documentUtils";
import { colorFromText } from "utils/colorFromText";
import { formatBytes } from "utils/formatBytes";
import { formatDisplayDate } from "utils/dateUtils";

// Enhanced file type configurations with modern colors and gradients
const FILE_TYPES = {
  PDF: {
    extensions: [".pdf"],
    icon: DocumentIcon,
    color: "text-red-600",
    bgColor: "bg-gradient-to-br from-red-50 to-red-100",
    borderColor: "border-red-200",
    darkBgColor: "dark:from-red-900/20 dark:to-red-800/30",
    label: "PDF Document",
  },
  DOCX: {
    extensions: [".docx", ".doc"],
    icon: DocumentIcon,
    color: "text-blue-600",
    bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
    borderColor: "border-blue-200",
    darkBgColor: "dark:from-blue-900/20 dark:to-blue-800/30",
    label: "Word Document",
  },
  IMAGE: {
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    icon: PhotoIcon,
    color: "text-green-600",
    bgColor: "bg-gradient-to-br from-green-50 to-green-100",
    borderColor: "border-green-200",
    darkBgColor: "dark:from-green-900/20 dark:to-green-800/30",
    label: "Image File",
  },
  EXCEL: {
    extensions: [".xlsx", ".xls"],
    icon: DocumentIcon,
    color: "text-emerald-600",
    bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
    borderColor: "border-emerald-200",
    darkBgColor: "dark:from-emerald-900/20 dark:to-emerald-800/30",
    label: "Excel Spreadsheet",
  },
  POWERPOINT: {
    extensions: [".pptx", ".ppt"],
    icon: DocumentIcon,
    color: "text-orange-600",
    bgColor: "bg-gradient-to-br from-orange-50 to-orange-100",
    borderColor: "border-orange-200",
    darkBgColor: "dark:from-orange-900/20 dark:to-orange-800/30",
    label: "PowerPoint Presentation",
  },
  DEFAULT: {
    extensions: [],
    icon: DocumentIcon,
    color: "text-gray-600",
    bgColor: "bg-gradient-to-br from-gray-50 to-gray-100",
    borderColor: "border-gray-200",
    darkBgColor: "dark:from-gray-900/20 dark:to-gray-800/30",
    label: "Document",
  },
};

// Utility function to get file type category
const getFileTypeCategory = (fileName) => {
  if (!fileName || typeof fileName !== "string") {
    return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
  }

  const parts = fileName.split(".");
  if (parts.length < 2) {
    return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
  }

  const extension = "." + parts.pop().toLowerCase();

  for (const [category, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(extension)) {
      return { category, config };
    }
  }
  return { category: "UNKNOWN", config: FILE_TYPES.DEFAULT };
};

// Utility function to format date
const formatDate = (dateString) => {
  return formatDisplayDate(dateString);
};

// Helper functions for history display
const getActionTypeColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "success";
    case 2: // Rejected
      return "error";
    case 3: // Final Approved
      return "primary";
    default:
      return "neutral";
  }
};

const getActionTypeBgColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "bg-green-100 dark:bg-green-900/30";
    case 2: // Rejected
      return "bg-red-100 dark:bg-red-900/30";
    case 3: // Final Approved
      return "bg-blue-100 dark:bg-blue-900/30";
    default:
      return "bg-gray-100 dark:bg-gray-900/30";
  }
};

const getActionTypeDotColor = (actionType) => {
  switch (actionType) {
    case 1: // Verified
      return "bg-green-500";
    case 2: // Rejected
      return "bg-red-500";
    case 3: // Final Approved
      return "bg-blue-500";
    default:
      return "bg-gray-500";
  }
};

export function ViewDocumentModal({
  isOpen,
  onClose,
  document,
  onDownload,
  onOpenInNewTab,
  onVerificationAction,
  isStudent = false,
}) {
  const [imageError, setImageError] = useState(false);

  // Reset image error when document changes
  useEffect(() => {
    setImageError(false);
  }, [document]);

  if (!document) return null;

  const { category, config } = getFileTypeCategory(document.name);
  const Icon = config.icon;
  const isImage = category === "IMAGE";
  const fileSize = document.size || 0;
  const uploadDate = document.createdAt || document.uploadedAt;
  const uploadedBy =
    document.uploadedBy || document.createdBy || "Unknown User";
  const filePath = document.filePath || "";
  const verificationInfo = getVerificationMetadataLabels(
    document.verificationStatus,
  );
  const hasVerificationMeta =
    document.verifiedBy || document.verifiedAt || document.verificationRemark;

  // Check if this document is from history
  const isHistoryDocument = document.isHistoryItem || document.History;



  const handleDownload = () => {
    if (onDownload) {
      onDownload(document);
    }
  };

  const handleOpenInNewTab = () => {
    if (onOpenInNewTab) {
      onOpenInNewTab(document);
    } else {
      // Fallback to direct window.open
      window.open(getDocumentUrl(filePath), "_blank");
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Safe formatting function for file size
  const safeFormatBytes = (bytes) => {
    try {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) {
        return "Unknown size";
      }
      return formatBytes(bytes);
    } catch (error) {
      console.warn("Error formatting bytes:", error);
      return "Unknown size";
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog open={true} onClose={() => { }} static autoFocus>
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 z-[300] bg-gray-900/60 backdrop-blur-sm transition-opacity"
        />

        <TransitionChild
          as={DialogPanel}
          enter="ease-out transform-gpu transition-all duration-300"
          enterFrom="opacity-0 scale-95 translate-y-4"
          enterTo="opacity-100 scale-100 translate-y-0"
          leave="ease-in transform-gpu transition-all duration-200"
          leaveFrom="opacity-100 scale-100 translate-y-0"
          leaveTo="opacity-0 scale-95 translate-y-4"
          className="fixed inset-0 z-[301] flex items-center justify-center p-4"
        >
          {/* Responsive Modal Container */}
          <div className="flex h-[90vh] w-full max-w-5xl transform-gpu flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900 sm:rounded-2xl sm:shadow-2xl">
            {/* Responsive Header - Fixed Height */}
            <div className="flex h-16 flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900 sm:h-18 sm:px-6">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor} sm:h-12 sm:w-12 sm:rounded-xl`}>
                  <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${config.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 truncate dark:text-gray-100 sm:text-xl">
                    Document Preview
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    {isHistoryDocument ? null : (
                      <Badge variant="soft" color={verificationInfo.color} className="text-[10px]">
                        {verificationInfo.label}
                      </Badge>
                    )}
                    <span className="text-xs text-gray-500 truncate dark:text-gray-400">
                      {config.label}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="flat"
                  size="sm"
                  onClick={handleOpenInNewTab}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 sm:hidden"
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="flat"
                  size="sm"
                  onClick={onClose}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                >
                  <XMarkIcon className="h-5 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>
            </div>

            {/* Responsive Content Area - Flex 1 with proper overflow */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full">
                {/* Mobile Layout - Stacked */}
                <div className="flex h-full flex-col lg:hidden">
                  {/* Mobile Document Info - Scrollable */}
                  <div className="flex-shrink-0 max-h-[35vh] overflow-y-auto border-b border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="space-y-4">
                      {/* Document Name */}
                      <div className="flex items-start gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor} flex-shrink-0`}>
                          <Icon className={`h-5 w-5 ${config.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
                            {document.name || document.FileName || 'Untitled Document'}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {safeFormatBytes(fileSize)} • {config.label}
                          </p>
                        </div>
                      </div>

                      {/* Essential Info Only for Mobile */}
                      <div className="grid grid-cols-1 gap-3 text-xs">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-400">Size:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{safeFormatBytes(fileSize)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-400">Status:</span>
                          <Badge variant="soft" color={verificationInfo.color} className="text-[10px]">
                            {verificationInfo.label}
                          </Badge>
                        </div>
                      </div>

                      {/* Document History - Show for history documents */}
                      {Boolean(isHistoryDocument) && Array.isArray(document.History) && document.History.length > 0 && (
                        <div className="rounded-lg bg-white p-3 dark:bg-gray-900">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 text-sm">Document History</h4>
                          <div className="space-y-3">
                            {document.History.map((historyItem, index) => (
                              <div key={`${historyItem.Version}-${index}`} className="relative">
                                {/* Timeline Line */}
                                {index < document.History.length - 1 && (
                                  <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
                                )}

                                <div className="flex items-start space-x-3">
                                  {/* Timeline Dot */}
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getActionTypeBgColor(historyItem.ActionType)} flex items-center justify-center`}>
                                    <div className={`w-3 h-3 rounded-full ${getActionTypeDotColor(historyItem.ActionType)}`} />
                                  </div>

                                  {/* History Content */}
                                  <div className="min-w-0 flex-1 pb-4">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <Badge
                                        variant="soft"
                                        color={getActionTypeColor(historyItem.ActionType)}
                                        className="text-[11px]"
                                      >
                                        {historyItem.ActionTypeText}
                                      </Badge>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Version {historyItem.Version}
                                      </span>
                                    </div>

                                    <div className="flex items-center space-x-2 mt-2">
                                      <Avatar
                                        size={8}
                                        color={colorFromText(historyItem.ActionByName || "Unknown")}
                                      >
                                        {getInitials(historyItem.ActionByName || "Unknown")}
                                      </Avatar>
                                      <div className="min-w-0 flex-1">
                                        <p className="dark:text-dark-300 text-xs leading-tight text-gray-600">
                                          {historyItem.ActionByName || "Unknown"}
                                        </p>
                                        <p className="dark:text-dark-400 text-xs text-gray-500">
                                          {formatDate(historyItem.ActionAt)}
                                        </p>
                                      </div>
                                    </div>

                                    {historyItem.Remark && (
                                      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                                        <p className="text-xs text-gray-600 dark:text-gray-300">
                                          {historyItem.Remark}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Verification Details - Show for non-history documents */}
                      {!isHistoryDocument && Boolean(hasVerificationMeta) && (
                        <div className="rounded-lg bg-white p-3 dark:bg-gray-900">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2 text-sm">Verification Details</h4>
                          {document.verifiedBy && (
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar
                                size={8}
                                color={colorFromText(document.verifiedBy)}
                              >
                                {getInitials(document.verifiedBy)}
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{document.verifiedBy}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{verificationInfo.meta.title}</p>
                              </div>
                            </div>
                          )}
                          {document.verificationRemark && (
                            <p className="text-xs text-gray-600 dark:text-gray-300 bg-gray-50 p-2 rounded dark:bg-gray-800">
                              {document.verificationRemark}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mobile Preview Area */}
                  <div className="flex-1 overflow-hidden bg-white dark:bg-gray-900 min-h-0">
                    <div className="h-full overflow-auto p-4">
                      <div className="flex items-center justify-center min-h-full">
                        {isImage && filePath && !imageError ? (
                          <img
                            src={getDocumentUrl(filePath)}
                            alt={document.name}
                            className="max-w-full h-auto rounded-lg shadow-sm max-h-[50vh] object-contain"
                            onError={handleImageError}
                          />
                        ) : category === "PDF" && filePath ? (
                          <iframe
                            src={getDocumentUrl(filePath)}
                            title={document.name}
                            className="w-full h-[50vh] rounded-lg border border-gray-200 dark:border-gray-700"
                            frameBorder="0"
                          />
                        ) : category === "DOCX" && filePath ? (
                          <iframe
                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                              getDocumentUrl(filePath),
                            )}`}
                            title={document.name}
                            className="w-full h-[50vh] rounded-lg border border-gray-200 dark:border-gray-700"
                            frameBorder="0"
                          />
                        ) : (
                          <div className="text-center py-8">
                            <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-xl ${config.bgColor} ${config.darkBgColor} mb-4`}>
                              <Icon className={`h-8 w-8 ${config.color}`} />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                              Preview Not Available
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto">
                              Download the document to view its contents.
                            </p>
                            <Button
                              variant="solid"
                              color="primary"
                              onClick={handleDownload}
                              size="sm"
                            >
                              <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                              Download
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout - Side by Side */}
                <div className="hidden h-full lg:flex">
                  {/* Sidebar */}
                  <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <div className="h-full flex flex-col">
                      <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-4">
                          {/* Document Name Card */}
                          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                            <div className="flex items-start gap-3">
                              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor} flex-shrink-0`}>
                                <Icon className={`h-5 w-5 ${config.color}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-gray-900 truncate dark:text-gray-100">
                                  {document.name || document.FileName || 'Untitled Document'}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {config.label}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* File Information */}
                          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-xs">File Information</h4>
                            <div className="space-y-3">
                              <InfoItem
                                icon={FolderIcon}
                                label="File Size"
                                value={safeFormatBytes(fileSize)}
                              />
                              <InfoItem
                                icon={ClockIcon}
                                label="Upload Date"
                                value={formatDate(uploadDate)}
                              />
                              <InfoItem
                                icon={UserIcon}
                                label="Uploaded By"
                                value={uploadedBy}
                              />
                              <InfoItem
                                icon={EyeIcon}
                                label={isHistoryDocument ? "Document Type" : "Verification Status"}
                                value={
                                  isHistoryDocument ? (
                                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                      History Document
                                    </span>
                                  ) : (
                                    <Badge variant="soft" color={verificationInfo.color} className="text-[10px]">
                                      {verificationInfo.label}
                                    </Badge>
                                  )
                                }
                              />
                            </div>
                          </div>

                          {/* Document History - Show for history documents */}
                          {Boolean(isHistoryDocument) && Array.isArray(document.History) && document.History.length > 0 && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-xs flex items-center gap-2">
                                <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
                                Document History
                              </h4>
                              <div className="space-y-3">
                                {document.History.map((historyItem, index) => (
                                  <div key={`${historyItem.Version}-${index}`} className="relative">
                                    {/* Timeline Line */}
                                    {index < document.History.length - 1 && (
                                      <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
                                    )}

                                    <div className="flex items-start space-x-3">
                                      {/* Timeline Dot */}
                                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getActionTypeBgColor(historyItem.ActionType)} flex items-center justify-center`}>
                                        <div className={`w-3 h-3 rounded-full ${getActionTypeDotColor(historyItem.ActionType)}`} />
                                      </div>

                                      {/* History Content */}
                                      <div className="min-w-0 flex-1 pb-4">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <Badge
                                            variant="soft"
                                            color={getActionTypeColor(historyItem.ActionType)}
                                            className="text-[11px]"
                                          >
                                            {historyItem.ActionTypeText}
                                          </Badge>
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Version {historyItem.Version}
                                          </span>
                                        </div>

                                        <div className="flex items-center space-x-2 mt-2">
                                          <Avatar
                                            size={8}
                                            color={colorFromText(historyItem.ActionByName || "Unknown")}
                                          >
                                            {getInitials(historyItem.ActionByName || "Unknown")}
                                          </Avatar>
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                              {historyItem.ActionByName || "Unknown"}
                                            </p>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                              {formatDate(historyItem.ActionAt)}
                                            </p>
                                          </div>
                                        </div>

                                        {historyItem.Remark && (
                                          <div className="rounded-lg bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                            {historyItem.Remark}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Verification Details */}
                          {Boolean(hasVerificationMeta) && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-xs flex items-center gap-2">
                                <CheckCircleIcon className="h-3.5 w-3.5 text-gray-400" />
                                Verification Details
                              </h4>
                              <div className="space-y-2">
                                {document.verifiedBy && (
                                  <div className="flex items-center gap-2">
                                    <Avatar
                                      size={8}
                                      color={colorFromText(document.verifiedBy)}
                                    >
                                      {getInitials(document.verifiedBy)}
                                    </Avatar>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                                        {document.verifiedBy}
                                      </p>
                                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                        {verificationInfo.meta.title}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {document.verifiedAt && (
                                  <div className="text-xs text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">{verificationInfo.meta.dateLabel}: </span>
                                    {formatDate(document.verifiedAt)}
                                  </div>
                                )}
                                {document.verificationRemark && (
                                  <div className="rounded-lg bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                    <span className="font-medium">{verificationInfo.meta.remarkLabel}: </span>
                                    {document.verificationRemark}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Verification Actions in View Modal */}
                          {Boolean(document?.canApproveOrReject) && (
                            <div className={clsx(
                              "grid gap-2 mt-4",
                              verificationInfo.value === 0 ? "grid-cols-2" : "grid-cols-1"
                            )}>
                              {(verificationInfo.value === 0 || verificationInfo.value === 2) && (
                                <Button
                                  variant="soft"
                                  color="success"
                                  size="sm"
                                  onClick={(e) => {
                                    if (isStudent) return;
                                    e.stopPropagation();
                                    onClose();
                                    onVerificationAction?.(document, "approve");
                                  }}
                                  disabled={isStudent}
                                  className={clsx("w-full", isStudent && "opacity-60 cursor-not-allowed")}
                                  title={isStudent ? "Cannot approve document for a student" : "Approve Document"}
                                >
                                  <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                                  Approve
                                </Button>
                              )}
                              {(verificationInfo.value === 0 || verificationInfo.value === 1) && (
                                <Button
                                  variant="soft"
                                  color="error"
                                  size="sm"
                                  onClick={(e) => {
                                    if (isStudent) return;
                                    e.stopPropagation();
                                    onClose();
                                    onVerificationAction?.(document, "reject");
                                  }}
                                  disabled={isStudent}
                                  className={clsx("w-full", isStudent && "opacity-60 cursor-not-allowed")}
                                  title={isStudent ? "Cannot reject document for a student" : "Reject Document"}
                                >
                                  <XCircleIcon className="h-4 w-4 mr-1.5" />
                                  Reject
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Description */}
                          {Boolean(document.description) && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-xs">Description</h4>
                              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Preview Area */}
                  <div className="flex-1 bg-white dark:bg-gray-900 min-h-0">
                    <div className="h-full overflow-hidden p-3">
                      <div className="h-full flex items-center justify-center">
                        {isImage && filePath && !imageError ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src={getDocumentUrl(filePath)}
                              alt={document.name}
                              className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-lg"
                              onError={handleImageError}
                            />
                            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg dark:bg-gray-800/90">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {config.label}
                              </span>
                            </div>
                          </div>
                        ) : category === "PDF" && filePath ? (
                          <div className="w-full h-full">
                            <iframe
                              src={getDocumentUrl(filePath)}
                              title={document.name}
                              className="w-full h-[60vh] rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                              frameBorder="0"
                            />
                          </div>
                        ) : category === "DOCX" && filePath ? (
                          <div className="w-full h-full">
                            <iframe
                              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                getDocumentUrl(filePath),
                              )}`}
                              title={document.name}
                              className="w-full h-[60vh] rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                              frameBorder="0"
                            />
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ${config.bgColor} ${config.darkBgColor} shadow-lg mb-4`}>
                              <Icon className={`h-10 w-10 ${config.color}`} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                              Preview Not Available
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                              This file type cannot be previewed in the browser. Please download the document to view its contents.
                            </p>
                            <Button
                              variant="solid"
                              color="primary"
                              onClick={handleDownload}
                              className="shadow-lg"
                            >
                              <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                              Download Document
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsive Footer - Fixed Height */}
            <div className="flex h-20 flex-shrink-0 items-center border-t border-gray-200 bg-gray-50 px-4 dark:border-gray-700 dark:bg-gray-800 sm:h-20 sm:px-6">
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
                  {safeFormatBytes(fileSize)} • {config.label}
                </div>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  {/* Mobile: Show Close and Download only */}
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={onClose}
                    size="sm"
                    className="flex-1 sm:flex-none"
                  >
                    Close
                  </Button>
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleDownload}
                    size="sm"
                    className="flex-1 sm:flex-none shadow-sm"
                  >
                    <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                  {/* Desktop: Show Open in New Tab */}
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenInNewTab}
                    size="sm"
                    className="hidden sm:flex"
                  >
                    <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
                    Open
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

// Helper component for info items
function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">{label}</p>
        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{value}</div>
      </div>
    </div>
  );
}

export default ViewDocumentModal